// AccountSessionContext — the only UI source of "am I logged in".
// Reads GET /v1/me. A local flag or leftover localStorage token is not truth.

import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react'
import { ApiError } from '../api/client'
import { type MeSnapshot, fetchMe, logoutSession, onSessionChanged } from '../api/userland'

export type AccountSessionStatus = 'loading' | 'guest' | 'ready'

export type AccountSessionValue = {
  status: AccountSessionStatus
  me: MeSnapshot | null
  reload: () => Promise<void>
  logout: () => Promise<void>
}

const Ctx = createContext<AccountSessionValue | null>(null)
const ME_SNAPSHOT_KEY = 'wils.me.snapshot'

function readRememberedMe(): MeSnapshot | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(ME_SNAPSHOT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<MeSnapshot>
    if (parsed.authenticated !== true || !parsed.account_id || !parsed.session_id) return null
    return parsed as MeSnapshot
  } catch {
    return null
  }
}

function writeRememberedMe(snapshot: MeSnapshot | null): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    if (!snapshot) sessionStorage.removeItem(ME_SNAPSHOT_KEY)
    else sessionStorage.setItem(ME_SNAPSHOT_KEY, JSON.stringify(snapshot))
  } catch {
    // storage unavailable
  }
}

export function AccountSessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AccountSessionStatus>(() =>
    readRememberedMe() ? 'ready' : 'loading'
  )
  const [me, setMe] = useState<MeSnapshot | null>(() => readRememberedMe())

  const reload = useMemo(
    () => async () => {
      try {
        const snapshot = await fetchMe()
        writeRememberedMe(snapshot)
        setMe(snapshot)
        setStatus('ready')
      } catch (err) {
        writeRememberedMe(null)
        setMe(null)
        setStatus(err instanceof ApiError && err.status === 401 ? 'guest' : 'guest')
      }
    },
    []
  )

  useEffect(() => {
    void reload()
    return onSessionChanged(() => {
      setStatus((current) => (current === 'guest' ? 'loading' : current))
      void reload()
    })
  }, [reload])

  const value = useMemo<AccountSessionValue>(
    () => ({
      status,
      me,
      reload,
      logout: async () => {
        await logoutSession()
        writeRememberedMe(null)
        setMe(null)
        setStatus('guest')
      },
    }),
    [me, reload, status]
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAccountSession(): AccountSessionValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAccountSession must be used inside <AccountSessionProvider>')
  return ctx
}

export function useIsGuest(): boolean {
  return useAccountSession().status === 'guest'
}

export function useOptionalAccountSession(): AccountSessionValue | null {
  return useContext(Ctx)
}
